import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import './components.css';

export interface Column<T> {
  key: string;
  header: string;
  width?: string;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  render: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number;
}

interface TableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string;
  empty?: ReactNode;
  density?: 'normal' | 'compact';
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
}

export function Table<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  selectedKey,
  empty,
  density = 'normal',
  defaultSort,
}: TableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | undefined>(
    defaultSort,
  );

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col || !col.sortValue) return rows;
    const out = [...rows].sort((a, b) => {
      const va = col.sortValue!(a);
      const vb = col.sortValue!(b);
      if (va < vb) return -1;
      if (va > vb) return 1;
      return 0;
    });
    return sort.dir === 'asc' ? out : out.reverse();
  }, [rows, sort, columns]);

  const onHeaderClick = (col: Column<T>) => {
    if (!col.sortValue) return;
    setSort((cur) => {
      if (!cur || cur.key !== col.key) return { key: col.key, dir: 'asc' };
      if (cur.dir === 'asc') return { key: col.key, dir: 'desc' };
      return undefined;
    });
  };

  return (
    <div className={`iris-table-wrap density-${density}`}>
      <table className="iris-table">
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.key === c.key;
              return (
                <th
                  key={c.key}
                  style={{
                    width: c.width,
                    textAlign: c.align ?? 'left',
                    cursor: c.sortValue ? 'pointer' : 'default',
                  }}
                  onClick={() => onHeaderClick(c)}
                >
                  <span className="iris-table-header-cell">
                    {c.header}
                    {c.sortValue && (
                      <span
                        className="iris-table-sort"
                        style={{ opacity: active ? 1 : 0.35 }}
                      >
                        {active && sort?.dir === 'desc' ? (
                          <ChevronDown size={12} strokeWidth={1.5} />
                        ) : (
                          <ChevronUp size={12} strokeWidth={1.5} />
                        )}
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="iris-table-empty">
                {empty ?? 'No rows.'}
              </td>
            </tr>
          )}
          {sorted.map((row) => {
            const k = rowKey(row);
            return (
              <tr
                key={k}
                onClick={() => onRowClick?.(row)}
                className={selectedKey === k ? 'selected' : ''}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    style={{
                      textAlign: c.align ?? 'left',
                    }}
                    className={c.mono ? 'mono' : ''}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
